import { Badge, Button, Field, Input, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface Pricing {
  addons: Record<'META_API', { monthly: number; stripePriceId?: string | null }>;
  usdToBrl: number;
  creditPacksCents: number[];
  minCreditCents: number;
  metaMessagePrices: Record<'MARKETING' | 'UTILITY' | 'AUTHENTICATION', number>;
}

const toBRL = (cents: number) => (cents / 100).toFixed(2).replace('.', ',');
const toCents = (v: string) => Math.round(Number(v.replace(/\./g, '').replace(',', '.')) * 100);
// Preço por mensagem da Meta: até 4 casas (R$ 0,3125)
const fromReais = (v: number) => String(v).replace('.', ',');
const toReais = (v: string) => Number(v.trim().replace(',', '.'));

/** Plataforma › Planos: preços das ferramentas e crédito manual para uma organização */
export function AddonPricingCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [form, setForm] = useState({ meta: '', usd: '', packs: '', min: '', mkt: '', util: '', auth: '' });
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [grant, setGrant] = useState({ tenantId: '', amount: '', description: '' });
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools/pricing');
    if (!res.ok) return;
    const p: Pricing = (await res.json()).pricing;
    setPricing(p);
    setForm({
      meta: String(p.addons.META_API.monthly).replace('.', ','),
      usd: String(p.usdToBrl).replace('.', ','),
      packs: p.creditPacksCents.map(toBRL).join('; '),
      min: toBRL(p.minCreditCents),
      mkt: fromReais(p.metaMessagePrices.MARKETING),
      util: fromReais(p.metaMessagePrices.UTILITY),
      auth: fromReais(p.metaMessagePrices.AUTHENTICATION),
    });
  }, [fetchApi]);

  useEffect(() => {
    load();
    fetchApi('/api/tenants').then((r) => (r.ok ? r.json() : null)).then((d) => d && setTenants(d.tenants || [])).catch(() => undefined);
  }, [load, fetchApi]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        addons: {
          META_API: { monthly: Number(form.meta.replace(',', '.')) },
        },
        usdToBrl: Number(form.usd.replace(',', '.')),
        minCreditCents: toCents(form.min),
        creditPacksCents: form.packs.split(';').map((v) => toCents(v.trim())).filter((n) => n > 0),
        metaMessagePrices: { MARKETING: toReais(form.mkt), UTILITY: toReais(form.util), AUTHENTICATION: toReais(form.auth) },
      };
      const res = await fetchApi('/api/tools/pricing', { method: 'PUT', body: JSON.stringify(body) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Erro ao salvar');
      if (json.stripeError) toast.error(`Salvo, mas o Stripe recusou: ${json.stripeError}`);
      else toast.success('Preços das ferramentas salvos');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const giveCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    setGranting(true);
    try {
      const res = await fetchApi('/api/tools/credits/adjust', {
        method: 'POST',
        body: JSON.stringify({ tenantId: grant.tenantId, amountCents: toCents(grant.amount), description: grant.description || undefined, wallet: 'TOOLS' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Erro ao creditar');
      toast.success(`Crédito lançado. Saldo: ${(json.balanceCents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`);
      setGrant({ tenantId: grant.tenantId, amount: '', description: '' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao creditar');
    } finally {
      setGranting(false);
    }
  };

  if (!pricing) return null;

  return (
    <section className="brand-card" style={{ marginTop: 32 }}>
      <h4 className="text-base font-semibold">Preços das ferramentas</h4>
      <div style={{ color: 'var(--text-muted)', marginTop: 2, marginBottom: 16 }}>
        Add-ons mensais entram na assinatura da organização; o consumo avulso sai da carteira de créditos.
      </div>
      <div className="flex gap-2" style={{ marginBottom: 16 }}>
        <Badge variant={pricing.addons.META_API.stripePriceId ? 'green' : 'orange'}>
          API oficial: {pricing.addons.META_API.stripePriceId ? 'no Stripe' : 'fora do Stripe'}
        </Badge>
      </div>
      <form onSubmit={save} className="grid gap-4 md:grid-cols-3">
        <Field label="API oficial da Meta (R$/mês por número)">
          <Input onChange={(e) => setForm({ ...form, meta: e.target.value })} value={form.meta} />
        </Field>
        <Field label="Cotação do dólar (R$)">
          <Input onChange={(e) => setForm({ ...form, usd: e.target.value })} value={form.usd} />
        </Field>
        <Field label="Pacotes de créditos (R$, separados por ;)">
          <Input onChange={(e) => setForm({ ...form, packs: e.target.value })} value={form.packs} />
        </Field>
        <Field label="Compra mínima (R$)">
          <Input onChange={(e) => setForm({ ...form, min: e.target.value })} value={form.min} />
        </Field>
        <div className="md:col-span-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div className="font-semibold">Custo por mensagem da API oficial (estimativa)</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Valor por template entregue que aparece na nova campanha e no relatório. A Meta cobra direto na conta do cliente.</div>
        </div>
        <Field label="Marketing (R$ por mensagem)">
          <Input onChange={(e) => setForm({ ...form, mkt: e.target.value })} inputMode="decimal" value={form.mkt} />
        </Field>
        <Field label="Utilidade (R$ por mensagem)">
          <Input onChange={(e) => setForm({ ...form, util: e.target.value })} inputMode="decimal" value={form.util} />
        </Field>
        <Field label="Autenticação (R$ por mensagem)">
          <Input onChange={(e) => setForm({ ...form, auth: e.target.value })} inputMode="decimal" value={form.auth} />
        </Field>
        <div className="md:col-span-3 flex justify-end">
          <Button type="submit" variant="primary" loading={saving}>Salvar preços</Button>
        </div>
      </form>

      <div style={{ borderTop: '1px solid var(--border)', marginTop: 24, paddingTop: 20 }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
          <div className="font-semibold">Crédito manual</div>
          <Badge variant="neutral">Cortesia ou acerto</Badge>
        </div>
        <form onSubmit={giveCredits} className="grid gap-4 md:grid-cols-3">
          <Field label="Organização">
            <Select value={((() => {
                const t = tenants.find((x) => x.id === grant.tenantId);
                return t ? { value: t.id, label: t.name } : null;
              })())?.value ?? null} items={tenants.map((t) => ({ value: t.id, label: t.name }))} onValueChange={(value: string | null) => setGrant({ ...grant, tenantId: value ?? '' })} placeholder="Escolha" />
          </Field>
          <Field label="Valor (R$)">
            <Input onChange={(e) => setGrant({ ...grant, amount: e.target.value })} inputMode="decimal" value={grant.amount} />
          </Field>
          <Field label="Descrição">
            <Input onChange={(e) => setGrant({ ...grant, description: e.target.value })} placeholder="Crédito manual da plataforma" value={grant.description} />
          </Field>
          <div className="md:col-span-3 flex justify-end">
            <Button type="submit" variant="secondary" loading={granting} disabled={!grant.tenantId || !(toCents(grant.amount || '0') > 0)}>Lançar crédito</Button>
          </div>
        </form>
      </div>
    </section>
  );
}
